import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
} from '@mui/material';
import {
  LENS_CONFIG,
  LENS_ORDER,
  RANGE_LABELS,
  RANGE_ORDER,
} from '../pages/habitStatsTypes';
import {
  useHabitStatsDefaultLens,
  useHabitStatsRangeSettings,
} from '../hooks/useHabitStatsPreferences';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

/**
 * Profile → Advanced Analytics: lets the user choose which Completion Trend
 * range presets appear and the default Health lens. Purely client-side
 * (localStorage); isolated from the Analytics Reference Date card.
 */
const HabitAnalyticsSettingsSection: React.FC = () => {
  const { enabledRanges, setRangeEnabled, resetToDefault } = useHabitStatsRangeSettings();
  const { defaultLens, setDefaultLens } = useHabitStatsDefaultLens();
  const { cardContentPaddingSx } = useLayoutTokens();

  const enabledCount = RANGE_ORDER.filter((r) => enabledRanges[r]).length;

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Habit Stats Chart
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Completion Trend Ranges
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose which range presets appear in the Habit Stats chart. At least one must stay
          enabled.
        </Typography>
        <FormGroup row>
          {RANGE_ORDER.map((option) => {
            const checked = Boolean(enabledRanges[option]);
            return (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    checked={checked}
                    // Prevent unchecking the last enabled preset.
                    disabled={checked && enabledCount === 1}
                    onChange={(e) => setRangeEnabled(option, e.target.checked)}
                    color="primary"
                  />
                }
                label={RANGE_LABELS[option]}
                sx={{ minWidth: 140 }}
              />
            );
          })}
        </FormGroup>

        <Box sx={{ mt: 2, maxWidth: 360 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Default Health Lens
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Default lens</InputLabel>
            <Select
              value={defaultLens}
              label="Default lens"
              onChange={(e) => setDefaultLens(e.target.value as typeof defaultLens)}
            >
              {LENS_ORDER.map((lens) => (
                <MenuItem key={lens} value={lens}>
                  {LENS_CONFIG[lens].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Button variant="outlined" onClick={resetToDefault} sx={{ mt: 2 }}>
          Reset to defaults
        </Button>
      </CardContent>
    </Card>
  );
};

export default HabitAnalyticsSettingsSection;
